import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Skeleton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import {
  FlightTakeoff,
  Build,
  Extension,
  Category
} from '@mui/icons-material';
import axios from 'axios';

const SummaryCard = ({ title, value, subtext, icon: Icon, color = 'primary' }) => (
  <Card sx={{ height: '100%', borderRadius: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {value}
          </Typography>
          {subtext != null && (
            <Typography variant="caption" color="text.secondary">
              {subtext}
            </Typography>
          )}
        </Box>
        {Icon && (
          <Box sx={{ bgcolor: `${color}.light`, color: `${color}.main`, p: 1.5, borderRadius: 2 }}>
            <Icon sx={{ fontSize: 28 }} />
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

const BarList = ({ items, labelKey = 'name', valueKey = 'count', emptyMessage }) => {
  const values = (items || []).map((i) => i[valueKey] || 0);
  const max = values.length ? Math.max(...values, 1) : 1;
  return (
    <List dense disablePadding>
      {!items?.length ? (
        <ListItem>
          <ListItemText primary={emptyMessage || 'No data'} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
        </ListItem>
      ) : (
        items.map((item, idx) => (
          <ListItem key={idx} disablePadding sx={{ py: 0.25 }}>
            <ListItemText
              primary={item[labelKey] ?? item.status ?? '—'}
              secondary={
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, ((item[valueKey] || 0) / max) * 100)}
                  sx={{ mt: 0.5, height: 6, borderRadius: 1, bgcolor: 'action.hover' }}
                  color="primary"
                />
              }
              primaryTypographyProps={{ variant: 'body2' }}
            />
            <Chip size="small" label={item[valueKey]} sx={{ ml: 1 }} />
          </ListItem>
        ))
      )}
    </List>
  );
};

/**
 * Aircraft Life-cycle Management Dashboard – summary of aircraft, assemblies, components
 */
const AircraftLifecycleDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get('/api/aircraft/summary');
        if (!cancelled) setSummary(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load summary');
          setSummary(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSummary();
    return () => { cancelled = true; };
  }, []);

  if (loading && !summary) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const data = summary || {};
  const byStatus = data.byStatus || [];
  const byAircraft = data.assembliesByAircraft || [];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom color="text.secondary">
        Overview
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard
            title="Aircraft"
            value={data.aircraftCount ?? 0}
            subtext="Active aircraft"
            icon={FlightTakeoff}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard
            title="Assemblies"
            value={data.totalAssemblies ?? 0}
            subtext="Total assemblies"
            icon={Build}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard
            title="Components"
            value={data.totalComponents ?? 0}
            subtext="Total components"
            icon={Extension}
            color="info"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Category fontSize="small" /> Assemblies by status
              </Typography>
              <BarList items={byStatus} labelKey="status" valueKey="count" emptyMessage="No assemblies" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FlightTakeoff fontSize="small" /> Assemblies by aircraft (top 10)
              </Typography>
              <BarList items={byAircraft} labelKey="name" valueKey="count" emptyMessage="No assemblies" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AircraftLifecycleDashboard;
